import { useState } from "react";
import {
  File,
  FileText,
  FileXls,
  FilePdf,
  FileZip,
  MusicNote,
  VideoCamera,
  DownloadSimple,
  X,
} from "@/components/ui/icons";
import { resolveAttachmentUrl } from "@/lib/attachments";
import { Button } from "@/components/ui/button";
import { openExternalUrl } from "@/lib/open-url";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

interface Attachment {
  id: string;
  filename: string;
  file_type: string;
  file_size: number;
  file_category: string;
  storage_key?: string | null;
  storage_url?: string | null;
  thumbnail_url?: string | null;
  width?: number | null;
  height?: number | null;
}

interface AttachmentPreviewProps {
  attachment: Attachment;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileIconProps {
  mimeType: string;
  category: string;
  size: number;
  className?: string;
}

function FileIcon({ mimeType, category, size, className }: FileIconProps) {
  if (mimeType.includes("pdf"))
    return <FilePdf size={size} className={className} />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return <FileXls size={size} className={className} />;
  if (
    mimeType.includes("text") ||
    mimeType.includes("document") ||
    mimeType.includes("word")
  )
    return <FileText size={size} className={className} />;
  if (mimeType.includes("zip") || mimeType.includes("tar"))
    return <FileZip size={size} className={className} />;
  if (category === "audio")
    return <MusicNote size={size} className={className} />;
  if (category === "video")
    return <VideoCamera size={size} className={className} />;
  return <File size={size} className={className} />;
}

function ImageAttachment({ attachment }: AttachmentPreviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const fullUrl = resolveAttachmentUrl(attachment);
  const displayUrl = attachment.thumbnail_url || fullUrl;

  if (imageError) {
    return <DocumentAttachment attachment={attachment} />;
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className={cn(
          "relative h-auto max-w-xs overflow-hidden rounded-lg p-0 shadow-sm shadow-overlay/10",
          "bg-secondary/70 hover:bg-secondary/70",
        )}
        style={
          attachment.width && attachment.height
            ? {
                aspectRatio: `${attachment.width} / ${attachment.height}`,
                maxHeight: 256,
                width: Math.min(
                  attachment.width,
                  Math.round(256 * (attachment.width / attachment.height)),
                  320,
                ),
              }
            : undefined
        }
      >
        <img
          src={displayUrl}
          alt={attachment.filename}
          className="w-full h-full object-contain"
          onError={() => setImageError(true)}
        />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-transparent border-none">
          <DialogTitle className="sr-only">{attachment.filename}</DialogTitle>
          <div className="relative">
            <DialogClose className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-overlay/50 hover:bg-overlay/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/70 focus-visible:ring-offset-2 focus-visible:ring-offset-overlay/50">
              <X size={20} className="text-foreground" />
            </DialogClose>
            <img
              src={fullUrl}
              alt={attachment.filename}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-overlay/50 to-transparent rounded-b-lg">
              <p className="text-sm text-foreground truncate">
                {attachment.filename}
              </p>
              <p className="text-xs text-foreground/70">
                {formatFileSize(attachment.file_size)}
                {attachment.width && attachment.height && (
                  <span className="ml-2">
                    {attachment.width} × {attachment.height}
                  </span>
                )}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function VideoAttachment({ attachment }: AttachmentPreviewProps) {
  return (
    <div
      className="max-w-md"
      style={
        attachment.width && attachment.height
          ? {
              aspectRatio: `${attachment.width} / ${attachment.height}`,
              maxHeight: 256,
              width: Math.min(
                attachment.width,
                Math.round(256 * (attachment.width / attachment.height)),
                448,
              ),
            }
          : undefined
      }
    >
      <video
        src={resolveAttachmentUrl(attachment)}
        controls
        className="rounded-lg w-full h-full bg-overlay"
        preload="metadata"
      >
        Your browser does not support the video tag.
      </video>
      <p className="text-xs text-muted-foreground mt-1 truncate">
        {attachment.filename} • {formatFileSize(attachment.file_size)}
      </p>
    </div>
  );
}

function AudioAttachment({ attachment }: AttachmentPreviewProps) {
  return (
    <div className="max-w-md" style={{ minHeight: 54 }}>
      <audio
        src={resolveAttachmentUrl(attachment)}
        controls
        className="w-full"
        preload="metadata"
      >
        Your browser does not support the audio tag.
      </audio>
      <p className="text-xs text-muted-foreground mt-1 truncate">
        {attachment.filename} • {formatFileSize(attachment.file_size)}
      </p>
    </div>
  );
}

function DocumentAttachment({ attachment }: AttachmentPreviewProps) {
  const url = resolveAttachmentUrl(attachment);
  return (
    <div className="flex items-center gap-2 w-full pb-3 mb-3">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          void openExternalUrl(url);
        }}
        className={cn(
          "group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer flex-1 min-w-0 max-w-xs",
          "bg-secondary/70 border border-border/80 shadow-sm shadow-overlay/10",
          "hover:bg-secondary hover:border-border transition-colors",
          "text-left no-underline",
        )}
      >
        <div className="shrink-0 p-2 rounded-lg bg-muted/50">
          <FileIcon
            mimeType={attachment.file_type}
            category={attachment.file_category}
            size={28}
            className="text-foreground"
          />
        </div>
        <p className="text-sm font-medium truncate text-foreground flex-1 min-w-0">
          {attachment.filename}
        </p>
        <DownloadSimple
          size={16}
          className="text-muted-foreground group-hover:text-foreground shrink-0 transition-colors"
        />
      </a>
      <p className="text-xs text-muted-foreground shrink-0">
        {formatFileSize(attachment.file_size)}
      </p>
    </div>
  );
}

export function AttachmentPreview({ attachment }: AttachmentPreviewProps) {
  switch (attachment.file_category) {
    case "image":
      return <ImageAttachment attachment={attachment} />;
    case "video":
      return <VideoAttachment attachment={attachment} />;
    case "audio":
      return <AudioAttachment attachment={attachment} />;
    default:
      return <DocumentAttachment attachment={attachment} />;
  }
}
